"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSensors = void 0;
const react_1 = require("react");
const mutations_1 = require("../graphql/mutations");
const queries_1 = require("../graphql/queries");
const subscriptions_1 = require("../graphql/subscriptions");
const types_1 = require("../types");
const amplifyBootstrap_1 = require("../utils/amplifyBootstrap");
const useSensors = (hubId) => {
    const [sensors, setSensors] = (0, react_1.useState)([]);
    const [hub, setHub] = (0, react_1.useState)();
    const [loading, setLoading] = (0, react_1.useState)(null);
    const [error, setError] = (0, react_1.useState)(null);
    const subscriptionsRef = (0, react_1.useRef)([]);
    const fetchHub = async (id) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getHub,
                variables: { id },
            });
            if (response && response.data && response.data.getHub) {
                setHub(response.data.getHub);
                return response.data.getHub;
            }
            return null;
        }
        catch (error) {
            console.error('Error fetching hub:', error);
        }
    };
    const fetchSensorsByHub = async (hubId) => {
        setLoading(true);
        setError(null);
        try {
            const hub = await fetchHub(hubId);
            if (!hub) {
                throw new Error(`Hub not found: ${hubId}`);
            }
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorsByHub,
                variables: { hubId, limit: 100 },
            });
            const sensors = response.data?.getSensorsByHub || [];
            setSensors(sensors);
        }
        catch (error) {
            console.error('Error fetching sensors:', error);
            setError(error);
            setSensors([]);
        }
        finally {
            setLoading(false);
        }
    };
    const fetchSensorsByGroupId = async (groupId) => {
        setLoading(true);
        setError(null);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorsByGroupId,
                variables: { groupId, limit: 100 },
            });
            const sensors = response.data?.getSensorsByGroupId || [];
            setSensors(sensors);
        }
        catch (error) {
            console.error('Error fetching sensors:', error);
            setError(error);
            setSensors([]);
        }
        finally {
            setLoading(false);
        }
    };
    const fetchAllSensors = async (limit = 100) => {
        setLoading(true);
        setError(null);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.listSensors,
                variables: { limit },
            });
            const sensors = response.data?.listSensors || [];
            setSensors(sensors);
        }
        catch (error) {
            console.error('Error fetching sensors:', error);
            setError(error);
            setSensors([]);
        }
        finally {
            setLoading(false);
        }
    };
    const getSensorById = async (id) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensor,
                variables: { id },
            });
            return response.data.getSensor;
        }
        catch (error) {
            console.error('Error fetching sensor:', error);
            throw error;
        }
    };
    const addSensor = async (inputOrParams) => {
        try {
            let sensorInput;
            if ('hwVersion' in inputOrParams && 'sensorType' in inputOrParams) {
                const { hubId, dsk, hwVersion, sensorType, serialNumber, name, location, sublocation, lastReported, equipmentDetails, } = inputOrParams;
                sensorInput = {
                    hubId,
                    dsk,
                    type: sensorType,
                    version: hwVersion,
                    serialNumber,
                    name: name || `Sensor ${serialNumber}`,
                    location: location || '',
                    status: types_1.SensorStatus.Inactive,
                    sublocation,
                    lastReported,
                    equipmentDetails,
                };
            }
            else {
                sensorInput = {
                    ...inputOrParams,
                    status: inputOrParams.status || types_1.SensorStatus.Inactive,
                    location: inputOrParams.location || '',
                    name: inputOrParams.name || `Sensor ${inputOrParams.serialNumber}`,
                    version: inputOrParams.version,
                };
            }
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.createSensor,
                variables: { input: sensorInput },
            });
            return response.data.createSensor;
        }
        catch (error) {
            console.error('Error creating sensor:', error);
            throw error;
        }
    };
    const createSensorGroup = async (input) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.createSensorGroup,
                variables: { input },
            });
            return response.data.createSensorGroup;
        }
        catch (error) {
            console.error('Error creating sensor group:', error);
            throw error;
        }
    };
    const updateSensor = async (input) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.updateSensor,
                variables: { input },
            });
            return response.data.updateSensor;
        }
        catch (error) {
            console.error('Error updating sensor:', error);
            throw error;
        }
    };
    const updateSensorBranchCircuits = async (input) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.updateSensorBranchCircuits,
                variables: { input },
            });
            return response.data.updateSensorBranchCircuits;
        }
        catch (error) {
            console.error('Error updating sensor branch circuits:', error);
            throw error;
        }
    };
    const removeSensor = async (id) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.deleteSensor,
                variables: { id },
            });
            return response.data.deleteSensor;
        }
        catch (error) {
            console.error('Error deleting sensor:', error);
            throw error;
        }
    };
    const clear = () => {
        setSensors([]);
        setLoading(null);
    };
    const fetchAndSubscribe = () => {
        if (!hubId) {
            return;
        }
        fetchSensorsByHub(hubId);
        try {
            const createSubscription = amplifyBootstrap_1.API.graphql({
                query: subscriptions_1.onCreateSensor,
            }).subscribe({
                next: ({ data }) => {
                    const newSensor = data.onSensorCreated;
                    if (newSensor && newSensor.hubId === hubId) {
                        setSensors((prevSensors) => [...prevSensors, newSensor]);
                        fetchHub(hubId);
                    }
                },
                error: (error) => console.error('Create subscription error:', error),
            });
            const updateSubscription = amplifyBootstrap_1.API.graphql({
                query: subscriptions_1.onUpdateSensor,
            }).subscribe({
                next: ({ data }) => {
                    const updatedSensor = data.onSensorUpdated;
                    if (updatedSensor) {
                        setSensors((prevSensors) => prevSensors.map((sensor) => sensor.id === updatedSensor.id ? updatedSensor : sensor));
                        fetchHub(hubId);
                    }
                },
                error: (error) => console.error('Update subscription error:', error),
            });
            const deleteSubscription = amplifyBootstrap_1.API.graphql({
                query: subscriptions_1.onDeleteSensor,
            }).subscribe({
                next: ({ data }) => {
                    const deletedSensor = data.onSensorDeleted;
                    if (deletedSensor) {
                        setSensors((prevSensors) => prevSensors.filter((sensor) => sensor.id !== deletedSensor.id));
                        fetchHub(hubId);
                        console.log('Sensor deleted:', deletedSensor);
                    }
                },
                error: (error) => console.error('Delete subscription error:', error),
            });
            subscriptionsRef.current = [createSubscription, updateSubscription, deleteSubscription];
        }
        catch (error) {
            console.error('Error setting up subscriptions:', error);
        }
    };
    const unsubscribe = () => {
        if (subscriptionsRef.current.length === 0) {
            return;
        }
        try {
            subscriptionsRef.current.forEach((subscription) => {
                if (subscription && typeof subscription.unsubscribe === 'function') {
                    subscription.unsubscribe();
                }
            });
            subscriptionsRef.current = [];
        }
        catch (error) {
            console.error('Error cleaning up subscriptions:', error);
        }
    };
    (0, react_1.useEffect)(() => {
        unsubscribe();
        fetchAndSubscribe();
        return () => {
            unsubscribe();
        };
    }, [hubId]);
    return {
        sensors,
        hub,
        loading,
        error,
        getSensorById,
        addSensor,
        createSensorGroup,
        updateSensor,
        updateSensorBranchCircuits,
        removeSensor,
        clear,
        fetchAndSubscribe,
        fetchSensorsByHub,
        fetchAllSensors,
        fetchSensorsByGroupId,
        unsubscribe,
    };
};
exports.useSensors = useSensors;
